import React from 'react';

import { useTheme } from '@mui/material';

import {
    AlertIcon,
    AlertLightIcon,
    AssessmentIcon,
    AssessmentLightIcon,
    DatasetsIcon,
    DatasetsLightIcon,
    ExploitsIcon,
    ExploitsLightIcon,
    McpIcon,
    McpLightIcon,
    VulnerabilityIntelligenceIcon,
    VulnerabilityIntelligenceLightIcon
} from 'pages/Landings/components/Icons';
import Title from 'pages/Landings/components/Title';
import { useSiteNavigation } from 'pages/Landings/hooks/use-site-navigation';
import { isLightMode } from 'pages/Landings/utils';

import Card from './components/Card';
import useStyles from './styles';

interface Props {
    untitled?: boolean;
    className?: string;
}

const ProductsSection: React.FC<Props> = ({ untitled = false, className }) => {
    const { cx, classes } = useStyles();
    const navigation = useSiteNavigation();
    const theme = useTheme();
    const isLight = isLightMode(theme);

    const icons = {
        VulnerabilityIntelligence: isLight ? <VulnerabilityIntelligenceLightIcon /> : <VulnerabilityIntelligenceIcon />,
        Assessment: isLight ? <AssessmentLightIcon /> : <AssessmentIcon />,
        Datasets: isLight ? <DatasetsLightIcon /> : <DatasetsIcon />,
        Alerts: isLight ? <AlertLightIcon /> : <AlertIcon />,
        Exploits: isLight ? <ExploitsLightIcon /> : <ExploitsIcon />,
        Mcp: isLight ? <McpLightIcon /> : <McpIcon />
    };

    return (
        <section className={cx(classes.root, className)}>
            {untitled ? null : <Title variant="h2">{'Products'}</Title>}
            <div className={cx(classes.cards)}>
                <Card
                    icon={icons.VulnerabilityIntelligence}
                    title="Vulnerability Intelligence"
                    {...navigation.products.intelligence}
                    description="Query the Vulners database to get enriched context for CVE, including all metrics, exploitability, and correlated real-world risk signals."
                />
                <Card
                    icon={icons.Assessment}
                    title="Assessment"
                    {...navigation.products.assessment}
                    description="Instantly turn software inventories or host data into vulnerability reports. Make vulnerability assessment a seamless feature in your product."
                />
                <Card
                    icon={icons.Datasets}
                    title="Datasets"
                    {...navigation.products.datasets}
                    description="Fetch the connected world of vulnerability intelligence. Export full datasets and see how CVEs, advisories, exploits, and observations link together."
                />
                <Card
                    icon={icons.Alerts}
                    title="Alerts"
                    {...navigation.products.alerts}
                    description="Stay updated about vulnerabilities that matter to you with real-time push and pull notifications. Trigger actions automatically when new vulnerabilities appear."
                />
                <Card
                    icon={icons.Exploits}
                    title="Exploits"
                    {...navigation.products.exploits}
                    description="Track fresh PoCs and wild exploitation listings as they appear. Instantly see if they can affect your assets and what needs to be prioritized for remediation."
                />
                <Card
                    icon={icons.Mcp}
                    title="MCP"
                    {...navigation.products.mcp}
                    description="Use Vulners MCP to power your AI agents. Break through the LLM knowledge cut-off and use real-time vulnerability insight."
                />
            </div>
        </section>
    );
};

export default ProductsSection;
